import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardList, Check, X, Star, Package,
  RefreshCw, ChevronDown, ChevronUp, Search, SendHorizonal,
  AlertCircle,
} from "lucide-react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";

dayjs.extend(relativeTime);
dayjs.locale("es");

function getStoredUser() {
  try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
}

const VEHICLE_ICONS = { moto: "🏍️", bicicleta: "🚲", carro: "🚗", camion: "🚛", otro: "🚐" };

const STATUS_META = {
  pendiente:  { label: "Pendiente",  color: "bg-amber-100 text-amber-700"   },
  aprobado:   { label: "Aprobado",   color: "bg-emerald-100 text-emerald-700" },
  rechazado:  { label: "Rechazado",  color: "bg-red-100 text-red-700"       },
};

function ApplicationCard({ app, onRespond, responding }) {
  const [expanded, setExpanded]  = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [showReject, setShowReject] = useState(false);
  const ryder = app.ryder;
  const initials = (ryder?.user?.name || "?").slice(0, 2).toUpperCase();
  const isInvitation = app.type === "invitation";
  const isPending    = app.status === "pendiente";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${
        isPending ? "border-amber-200" : "border-gray-100"
      }`}
    >
      <div className="flex items-center gap-4 p-4">
        {/* Avatar */}
        <div className="relative shrink-0">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white font-bold">
            {initials}
          </div>
          <span className="absolute -bottom-1 -right-1 text-base leading-none">
            {VEHICLE_ICONS[ryder?.vehicleType] ?? "🚐"}
          </span>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-gray-900">{ryder?.user?.name ?? "Ryder"}</span>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_META[app.status]?.color}`}>
              {isInvitation ? "Invitación" : "Solicitud"} · {STATUS_META[app.status]?.label}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500 flex-wrap">
            <span className="flex items-center gap-1"><Star size={10} />{Number(ryder?.rating ?? 0).toFixed(1)}★</span>
            <span className="flex items-center gap-1"><Package size={10} />{ryder?.totalDeliveries ?? 0} entregas</span>
            <span>{ryder?.vehicleType}</span>
            <span>·</span>
            <span>{dayjs(app.createdAt).fromNow()}</span>
          </div>
          {app.message && (
            <p className="text-xs text-gray-600 mt-1 bg-gray-50 rounded-lg px-2 py-1 border border-gray-100 line-clamp-1">
              "{app.message}"
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {isPending && !isInvitation && (
            <>
              <button
                onClick={() => onRespond(app.id, "aprobado")}
                disabled={responding === app.id}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition disabled:opacity-50"
              >
                <Check size={13} />Aprobar
              </button>
              <button
                onClick={() => setShowReject(v => !v)}
                disabled={responding === app.id}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-red-300 text-red-600 rounded-xl hover:bg-red-50 transition disabled:opacity-50"
              >
                <X size={13} />Rechazar
              </button>
            </>
          )}
          {!isPending && app.responseNote && (
            <button onClick={() => setExpanded(v => !v)} className="text-gray-400 hover:text-gray-600">
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          )}
        </div>
      </div>

      {/* Reject note input */}
      <AnimatePresence>
        {showReject && isPending && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 border-t border-gray-100 pt-3">
              <label className="text-xs font-semibold text-gray-500 block mb-1.5">Motivo del rechazo (opcional)</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={rejectNote}
                  onChange={e => setRejectNote(e.target.value)}
                  placeholder="Ej: No hay cupo disponible por ahora"
                  className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-red-300"
                />
                <button
                  onClick={() => { onRespond(app.id, "rechazado", rejectNote); setShowReject(false); }}
                  disabled={responding === app.id}
                  className="px-3 py-2 bg-red-600 text-white text-xs font-bold rounded-xl hover:bg-red-700 transition disabled:opacity-50"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Response note (when decided) */}
      <AnimatePresence>
        {expanded && app.responseNote && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3 border-t border-gray-100 pt-2">
              <p className="text-xs text-gray-500"><span className="font-semibold">Nota:</span> {app.responseNote}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function InviteRyderModal({ companyId, onClose, onSent }) {
  const [query,   setQuery]   = useState("");
  const [ryders,  setRyders]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(null);
  const [message, setMessage] = useState("");

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await API.get(`/ryders?approvalStatus=aprobado&search=${encodeURIComponent(query)}`);
      setRyders(res.data?.data?.data ?? res.data?.data?.ryders ?? res.data?.ryders ?? []);
    } catch { toast.error("Error al buscar ryders"); }
    finally { setLoading(false); }
  };

  const invite = async (ryderId) => {
    setSending(ryderId);
    try {
      await API.post(`/delivery-companies/${companyId}/invite/${ryderId}`, { message });
      toast.success("Invitación enviada");
      onSent();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al enviar invitación");
    } finally { setSending(null); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900 flex items-center gap-2"><SendHorizonal size={16} className="text-indigo-600" />Invitar ryder</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X size={16} /></button>
        </div>

        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Buscar por nombre o email"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && search()}
              className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-indigo-300"
            />
          </div>
          <button onClick={search} className="px-4 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition">
            Buscar
          </button>
        </div>

        <textarea
          placeholder="Mensaje de invitación (opcional)"
          value={message}
          onChange={e => setMessage(e.target.value)}
          rows={2}
          className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-300 mb-3 resize-none"
        />

        <div className="max-h-64 overflow-y-auto space-y-2">
          {loading && <p className="text-center text-gray-400 text-sm py-4">Buscando...</p>}
          {!loading && ryders.length === 0 && query && (
            <p className="text-center text-gray-400 text-sm py-4">Sin resultados</p>
          )}
          {ryders.map(r => (
            <div key={r.id} className="flex items-center gap-3 p-3 border border-gray-100 rounded-xl hover:bg-gray-50">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                {(r.name || "?").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm truncate">{r.name}</p>
                <p className="text-xs text-gray-500">{r.vehicleType} · {Number(r.rating ?? 0).toFixed(1)}★</p>
              </div>
              <button
                onClick={() => invite(r.id)}
                disabled={sending === r.id}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition disabled:opacity-50"
              >
                {sending === r.id ? "..." : <><SendHorizonal size={11} />Invitar</>}
              </button>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

export default function DeliveryApplications() {
  const [company,      setCompany]      = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [responding,   setResponding]   = useState(null);
  const [tab,          setTab]          = useState("incoming"); // incoming | invitations
  const [statusFilter, setStatusFilter] = useState("pendiente");
  const [showInvite,   setShowInvite]   = useState(false);

  const fetchCompany = useCallback(async () => {
    try {
      const r = await API.get("/delivery-companies/me");
      return r.data?.data?.company ?? r.data?.company ?? null;
    } catch (err) {
      console.error("fetchCompany:", err);
      return null;
    }
  }, []);

  const fetchApplications = useCallback(async (companyId) => {
    try {
      const r = await API.get(`/delivery-companies/${companyId}/applications`);
      setApplications(r.data?.data?.applications ?? []);
    } catch { toast.error("Error al cargar solicitudes"); }
  }, []);

  useEffect(() => {
    fetchCompany().then(comp => {
      setCompany(comp);
      if (comp?.id) fetchApplications(comp.id).finally(() => setLoading(false));
      else setLoading(false);
    });
  }, [fetchCompany, fetchApplications]);

  const refresh = async () => {
    if (!company?.id) return;
    setRefreshing(true);
    await fetchApplications(company.id);
    setRefreshing(false);
  };

  const handleRespond = async (appId, status, responseNote = null) => {
    setResponding(appId);
    try {
      await API.patch(`/delivery-companies/${company.id}/applications/${appId}`, { status, responseNote });
      toast.success(status === "aprobado" ? "Ryder aprobado y vinculado a tu empresa" : "Solicitud rechazada");
      await fetchApplications(company.id);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al responder");
    } finally { setResponding(null); }
  };

  const incoming    = applications.filter(a => a.type === "application");
  const invitations = applications.filter(a => a.type === "invitation");
  const filtered    = (tab === "incoming" ? incoming : invitations).filter(a =>
    statusFilter === "all" ? true : a.status === statusFilter
  );

  const pendingCount = incoming.filter(a => a.status === "pendiente").length;

  return (
    <div className="p-4 lg:p-6 space-y-5 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-gray-900 flex items-center gap-2">
            <ClipboardList size={20} className="text-indigo-600" />
            Solicitudes
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">Ryders que quieren unirse a tu empresa</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              if (!company?.id) {
                toast.error("Tu perfil de empresa no está cargado. Recarga la página.");
                return;
              }
              setShowInvite(true);
            }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition shadow-sm"
          >
            <SendHorizonal size={14} />Invitar ryder
          </button>
          <button
            onClick={refresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition disabled:opacity-50"
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Tabs + filter */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-100">
          {[
            { key: "incoming",    label: "Solicitudes recibidas", count: pendingCount },
            { key: "invitations", label: "Invitaciones enviadas", count: invitations.filter(a => a.status === "pendiente").length },
          ].map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-semibold transition border-b-2
                ${tab === key
                  ? "border-indigo-500 text-indigo-700 bg-indigo-50/30"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"}`}
            >
              {label}
              {count > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1">
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-50">
          {["pendiente", "aprobado", "rechazado", "all"].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition ${
                statusFilter === s
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {s === "all" ? "Todos" : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {/* List */}
        <div className="p-4 space-y-3 min-h-[200px]">
          {loading ? (
            Array(3).fill(0).map((_, i) => (
              <div key={i} className="h-24 bg-gray-50 rounded-2xl animate-pulse border border-gray-100" />
            ))
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center text-gray-400">
              <AlertCircle size={36} className="mb-3 text-gray-200" />
              <p className="font-semibold text-gray-500">Sin {tab === "incoming" ? "solicitudes" : "invitaciones"} {statusFilter !== "all" ? statusFilter + "s" : ""}</p>
              <p className="text-xs mt-1">
                {tab === "incoming"
                  ? "Cuando un ryder te envíe una solicitud aparecerá aquí"
                  : "Usa 'Invitar ryder' para buscar y enviar invitaciones"}
              </p>
            </div>
          ) : (
            <AnimatePresence initial={false}>
              {filtered.map(app => (
                <ApplicationCard
                  key={app.id}
                  app={app}
                  onRespond={handleRespond}
                  responding={responding}
                />
              ))}
            </AnimatePresence>
          )}
        </div>
      </div>

      {showInvite && company?.id && (
        <InviteRyderModal
          companyId={company.id}
          onClose={() => setShowInvite(false)}
          onSent={() => { setShowInvite(false); refresh(); }}
        />
      )}

      {/* Aviso si no hay empresa de domicilio registrada */}
      {!loading && !company && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-700 flex items-start gap-3">
          <AlertCircle size={18} className="shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">No se encontró tu empresa de domicilio</p>
            <p className="text-xs mt-0.5 text-amber-600">Asegúrate de que tu perfil de empresa esté creado. Contacta al administrador si el problema persiste.</p>
          </div>
        </div>
      )}
    </div>
  );
}
